import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { acceptTerms as acceptTermsAPI, getTermsStatus } from "../services/ledgerService";
import { useAuth } from "./AuthContext";

const DISMISSED_STORAGE_KEY = "terms_modal_dismissed";

const TermsContext = createContext(null);

export function TermsProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [currentVersion, setCurrentVersion] = useState(null);
    const [acceptedVersion, setAcceptedVersion] = useState(null);
    const [dismissed, setDismissed] = useState(
        () => sessionStorage.getItem(DISMISSED_STORAGE_KEY) === "1"
    );
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            setLoaded(false);
            return;
        }
        let cancelled = false;
        getTermsStatus()
            .then((status) => {
                if (cancelled) return;
                setCurrentVersion(status.currentVersion);
                setAcceptedVersion(status.acceptedVersion);
                setLoaded(true);
            })
            .catch(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const accept = useCallback(async () => {
        const { acceptedVersion: v } = await acceptTermsAPI();
        setAcceptedVersion(v);
        setDismissed(false);
        sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
    }, []);

    const dismiss = useCallback(() => {
        setDismissed(true);
        sessionStorage.setItem(DISMISSED_STORAGE_KEY, "1");
    }, []);

    const reopen = useCallback(() => {
        setDismissed(false);
        sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
    }, []);

    const needsAcceptance = loaded && currentVersion && currentVersion !== acceptedVersion;

    return (
        <TermsContext.Provider value={{
            needsAcceptance,
            dismissed,
            currentVersion,
            acceptedVersion,
            accept,
            dismiss,
            reopen,
        }}>
            {children}
        </TermsContext.Provider>
    );
}

export function useTerms() {
    const ctx = useContext(TermsContext);
    if (!ctx) {
        throw new Error("useTerms must be used within a TermsProvider");
    }
    return ctx;
}
